import { useEffect, useState } from "preact/hooks"
import { Button } from "../../../../components/ui/button"
import { useAsyncEffect } from "../../../../utils"
import { UserState } from "@/auth/user"
import { ProductSearchResult, fetchProductsByIds } from "@/api/product_catalog"
import { Promotion, PromotionLine, PromotionStage, CalendarInfo } from '../types'
import { createPromotionTypeOptions } from '../constants'
import { validatePromotion } from '../validation'
import { promotion_fields_from_calendar } from '../calendar_prefill'
import { summarize_line_rates, summarize_charge_pct, format_rate_chip, format_charge_pct } from '../line_rates'
import { expected_trade_note, expected_trade_spend, format_trade_spend_formula } from '../planning_assumptions'
import {
  PromotionTypeLayout,
  has_unit_deal,
  layout_for_saved_promotion,
  planned_amount_help,
  promotion_type_layout,
  start_date_help,
  strip_fields_for_type,
} from '../promotion_type_layout'
import {
  cases_from_units,
  format_money,
  format_pct,
  line_totals,
  lines_for_save,
  margin_impact,
  promoted_price,
  shared_units_per_case,
  split_baseline_and_lift,
  suggested_regular_price,
  suggested_unit_cost,
  unit_discount,
  units_from_cases,
  volumes_disagree,
} from "../deal_math"
import { stage_options } from "../../promo_calendar/stage_permissions"
import { exactCurrencyFormatter } from "../../../../utils"
import { PromotionFormField, ArrayField, SelectField } from './form_fields'
import { api_fetch } from "../../../../api/client"
import { ProductMultiSelect } from "./product_multi_select"
import { PromotionAccountFields } from "./promotion_account_fields"
import { OwnershipSection } from "./ownership_section"
import { ProductDetails, PromotionLinesEditor } from "./promotion_lines_editor"
import { PromotionLinesTable } from "../../promo_calendar/components/PromotionLinesTable"
import { PromotionLastRunNotice } from "./last_run_notice"

/**
 * Catalog details (case pack, prices, cost) for the products on a form, loaded once per set of
 * picked ids and kept as products are added, so the deal math has what it needs.
 */
export function useProductDetails(product_ids: string[] | undefined): [ProductDetails, (product: ProductSearchResult) => void] {
  const [details, setDetails] = useState<ProductDetails>({})
  const missing = (product_ids ?? []).filter((id) => !details[id])
  const missing_key = missing.join(",")
  useEffect(() => {
    if (!missing_key) return
    let current = true
    fetchProductsByIds(missing_key.split(",")).then((response) => {
      if (!current || !response.ok) return
      setDetails((previous) => {
        const next = { ...previous }
        for (const product of response.value.data) next[product.id] = product
        return next
      })
    })
    return () => {
      current = false
    }
  }, [missing_key])
  function remember(product: ProductSearchResult) {
    setDetails((previous) => (previous[product.id] ? previous : { ...previous, [product.id]: product }))
  }
  return [details, remember]
}

interface PromotionEditFormProps {
  promotion: Promotion
  onSave: (promotion: Promotion) => void
  onCancel: () => void
  /** Per-product line items imported for this promotion (shown as a read-only section). */
  lines?: PromotionLine[]
  linesLoading?: boolean
}

export function PromotionEditForm({ promotion, onSave, onCancel, lines, linesLoading }: PromotionEditFormProps) {
  const [editedPromotion, setEditedPromotion] = useState<Promotion>(promotion)
  const [showMoreDetails, setShowMoreDetails] = useState(
    Boolean(
      (promotion.regions && promotion.regions.length > 0) ||
      (promotion.terms && promotion.terms.length > 0) ||
      (promotion.restrictions && promotion.restrictions.length > 0)
    )
  )
  const [promotionTypes, setPromotionTypes] = useState<Array<{ value: string; label: string }>>([])
  const [products, remember_product] = useProductDetails(editedPromotion.product_ids)
  const [showLines, setShowLines] = useState((promotion.lines ?? []).length > 0)
  const current_user = UserState.use()

  // While the type is the one saved, anything already filled stays visible; once the rep picks
  // another type the form follows that type, and the save drops what it does not take.
  const type_changed = (editedPromotion.promotion_type || "") !== (promotion.promotion_type || "")
  const layout = type_changed
    ? promotion_type_layout(editedPromotion.promotion_type)
    : layout_for_saved_promotion(promotion)

  // Fetch promotion types from API
  useAsyncEffect(async () => {
    const result = await api_fetch<string[]>('/promotion-types')
    if (result.ok) {
      const existingTypes = result.value.data
      setPromotionTypes(createPromotionTypeOptions(existingTypes))
    } else {
      console.error('Failed to fetch promotion types:', result.error)
      // Fallback to default types
      setPromotionTypes(createPromotionTypeOptions([]))
    }
  }, [])

  // Update editedPromotion when promotion prop changes
  useAsyncEffect(async () => {
    setEditedPromotion(promotion)
    setShowLines((promotion.lines ?? []).length > 0)
    setShowMoreDetails(
      Boolean(
        (promotion.regions && promotion.regions.length > 0) ||
        (promotion.terms && promotion.terms.length > 0) ||
        (promotion.restrictions && promotion.restrictions.length > 0)
      )
    )
  }, [promotion])

  function handleSave(stage?: PromotionStage) {
    const with_stage = stage ? { ...editedPromotion, stage } : editedPromotion
    // The per-product rows store the spend the form worked out for them whether or not the type changed.
    const to_save = type_changed ? strip_fields_for_type(with_stage) : { ...with_stage, lines: lines_for_save(with_stage) }
    if (validatePromotion(to_save)) {
      setEditedPromotion(with_stage)
      onSave(to_save)
    }
  }

  function handleCancel() {
    setEditedPromotion(promotion)
    onCancel()
  }

  const stage = editedPromotion.stage || "Pending"
  const stages = stage_options(current_user?.role, stage)

  return (
    <div class="border border-gray-200 rounded-lg p-4 bg-white">
      <div class="space-y-4">
        {/* Status: where the plan is in its approval, with the next step one click away. */}
        <div class="flex flex-wrap items-center gap-2 rounded-md border border-gray-200 bg-gray-50 p-3">
          <span class="text-sm font-medium text-gray-700">Status</span>
          <select
            class="rounded border border-gray-300 bg-white px-2 py-1 text-sm"
            value={stage}
            onChange={(event) => setEditedPromotion({ ...editedPromotion, stage: (event.target as HTMLSelectElement).value as PromotionStage })}
          >
            {stages.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <span class="flex-1" />
          {stage === "Pending" && stages.includes("Submitted") && (
            <Button size="sm" variant="outline" onClick={() => handleSave("Submitted")}>
              Submit for approval
            </Button>
          )}
          {stage === "Submitted" && stages.includes("Approved") && (
            <Button size="sm" onClick={() => handleSave("Approved")}>
              Approve
            </Button>
          )}
          {stage === "Submitted" && stages.includes("Pending") && (
            <Button size="sm" variant="outline" onClick={() => handleSave("Pending")}>
              Send back to draft
            </Button>
          )}
        </div>

        {/* Name */}
        <PromotionFormField
          label="Promotion Name"
          value={editedPromotion.name}
          onChange={(value) => setEditedPromotion({ ...editedPromotion, name: value as string })}
          required
        />

        {/* Remote Promotion ID */}
        <PromotionFormField
          label="Remote Promotion ID"
          value={editedPromotion.remote_promo_id || ""}
          onChange={(value) => setEditedPromotion({ ...editedPromotion, remote_promo_id: value as string })}
          placeholder="Assigned on create"
        />

        {/* Promotion Type */}
        <SelectField
          label="Promotion Type"
          value={editedPromotion.promotion_type || ""}
          // Changing type keeps what was entered: fields the new type does not use are hidden, and
          // dropped only when saving (strip_fields_for_type), so switching back loses nothing.
          onChange={(value) => setEditedPromotion({ ...editedPromotion, promotion_type: value })}
          options={promotionTypes}
          placeholder="Select promotion type"
          required
        />

        {/* Products */}
        <ProductMultiSelect
          label="Products"
          selectedIds={editedPromotion.product_ids || []}
          selectedLabels={[...editedPromotion.products].sort()}
          onChange={(productIds, products) => setEditedPromotion({ ...editedPromotion, product_ids: productIds, products })}
          onProductPicked={remember_product}
          required={!layout.products_optional}
          allowAllProducts={layout.products_optional}
        />

        {/* Customer and Retailer */}
        <div class="grid grid-cols-2 gap-3">
          <PromotionFormField
            label="Customer (Distributor)"
            value={editedPromotion.customer || ""}
            onChange={(value) => setEditedPromotion({ ...editedPromotion, customer: value as string })}
            placeholder="e.g., KeHE, UNFI, Direct"
            required
          />
          <PromotionFormField
            label="Retailer (Key Account)"
            value={editedPromotion.retailer || ""}
            onChange={(value) => setEditedPromotion({ ...editedPromotion, retailer: value as string })}
            placeholder="e.g., Whole Foods, Target"
          />
        </div>
        <p class="text-xs text-gray-500 mt-1">
          Customer is the distributor buying from the brand. Retailer is where consumers buy the product. 
          Use "Direct" for customer if same as retailer.
        </p>

        {/* Discount Fields. Imported promotions often have no promotion_type (which gates
            the editable discount fields) but still carry per-unit rates on their lines —
            show those rates either way. */}
        {editedPromotion.promotion_type ? (
          <DiscountSection
            promotion={editedPromotion}
            onChange={setEditedPromotion}
            layout={layout}
            lines={lines}
            products={products}
          />
        ) : (
          <LineRatesPanel lines={lines} heading="Discount Details" />
        )}

        {editedPromotion.promotion_type && has_unit_deal(layout) && (editedPromotion.product_ids?.length || editedPromotion.products.length) ? (
          <PerProductSection promotion={editedPromotion} onChange={setEditedPromotion} products={products} open={showLines} onToggle={setShowLines} />
        ) : null}

        {/* Planned Amount */}
        <PromotionFormField
          label="Planned Amount"
          value={editedPromotion.planned_amount}
          onChange={(value) => setEditedPromotion({ ...editedPromotion, planned_amount: value as number | undefined })}
          type="number"
          step="0.01"
          prefix="$"
          placeholder="Total expected spend for this promotion"
          help={planned_amount_help(layout)}
        />
        <PromotionLastRunNotice promotion={editedPromotion} />

        {/* Volume forecast and latest estimate */}
        <div class={layout.show_volume ? "grid grid-cols-3 gap-3" : "grid grid-cols-1 gap-3"}>
          {layout.show_volume && <VolumeFields promotion={editedPromotion} onChange={setEditedPromotion} products={products} />}
          <PromotionFormField
            label="Latest Estimate"
            value={editedPromotion.latest_estimate_amount}
            onChange={(value) => setEditedPromotion({ ...editedPromotion, latest_estimate_amount: value as number | undefined })}
            type="number"
            step="0.01"
            prefix="$"
            placeholder="Defaults to plan"
            help="In-year re-forecast of spend for this promotion. Leave blank to use the planned amount."
          />
        </div>

        {/* Lift and participation assumptions (editable on past promotions too) */}
        {layout.show_volume && <PlanningAssumptionsSection promotion={editedPromotion} onChange={setEditedPromotion} products={products} />}

        {/* Basic Details */}
        <TimingSection
          promotion={editedPromotion}
          onChange={setEditedPromotion}
          layout={layout}
        />

        <OwnershipSection
          promotion={editedPromotion}
          onChange={(changes) => setEditedPromotion({ ...editedPromotion, ...changes })}
          approverError={stage === "Submitted" && !editedPromotion.approver_id}
        />

        {/* More Details */}
        <MoreDetailsSection
          promotion={editedPromotion}
          onChange={setEditedPromotion}
          showMoreDetails={showMoreDetails}
          onToggleMoreDetails={setShowMoreDetails}
        />

        <div class="flex gap-2">
          <Button onClick={() => handleSave()}>Save Changes</Button>
          <Button onClick={handleCancel} variant="outline">Cancel</Button>
        </div>

        {/* Line items (read-only, from external TPM imports like Promomash) */}
        {promotion.id && (lines === undefined || lines.length > 0 || linesLoading) && (
          <div class="space-y-2 pt-2">
            <h4 class="text-sm font-semibold text-gray-900">Line Items</h4>
            <PromotionLinesTable lines={lines ?? []} loading={linesLoading} />
          </div>
        )}
      </div>
    </div>
  )
}

interface AddPromotionFormProps {
  calendar: CalendarInfo
  onSave: (promotion: Promotion) => void
  onCancel: () => void
  isSaving: boolean
}

export function AddPromotionForm({ calendar, onSave, onCancel, isSaving }: AddPromotionFormProps) {
  const [newPromotion, setNewPromotion] = useState<Promotion>({
    name: "",
    remote_promo_id: "",
    description: "",
    start_date: "",
    end_date: "",
    discount_amount: undefined,
    discount_percentage: undefined,
    multibuy_quantity: undefined,
    multibuy_price: undefined,
    promotion_type: "",
    products: [],
    product_ids: [],
    regions: [],
    terms: [],
    restrictions: [],
    customer: "",
    retailer: "",
    brand: "",
    // Customer/retailer/brand come from the calendar's existing promotions (the customer is often
    // a different party than the calendar's retailer — e.g. Big Y Foods bills through Bozzuto's).
    ...promotion_fields_from_calendar(calendar),
  })

  const [showMoreDetails, setShowMoreDetails] = useState(false)
  const [promotionTypes, setPromotionTypes] = useState<Array<{ value: string; label: string }>>([])
  const [products, remember_product] = useProductDetails(newPromotion.product_ids)
  const layout = promotion_type_layout(newPromotion.promotion_type)

  // Fetch promotion types from API
  useAsyncEffect(async () => {
    const result = await api_fetch<string[]>('/promotion-types')
    if (result.ok) {
      const existingTypes = result.value.data
      setPromotionTypes(createPromotionTypeOptions(existingTypes))
    } else {
      console.error('Failed to fetch promotion types:', result.error)
      // Fallback to default types
      setPromotionTypes(createPromotionTypeOptions([]))
    }
  }, [])

  function handleSave() {
    // Fields the chosen type does not use stay in the form (switching back keeps them) but are not saved.
    const to_save = strip_fields_for_type(newPromotion)
    if (validatePromotion(to_save)) {
      onSave(to_save)
    }
  }

  return (
    <div class="border border-gray-200 rounded-lg p-4 bg-gray-50 mt-4">
      <h5 class="text-sm font-medium text-gray-700 mb-3">Add New Promotion</h5>
      <div class="space-y-4">
        {/* Step 1: Name */}
        <PromotionFormField
          label="Promotion Name"
          value={newPromotion.name}
          onChange={(value) => setNewPromotion({ ...newPromotion, name: value as string })}
          placeholder="e.g., March TPR - Product Line"
          required
        />

        {/* Step 2: Promotion Type */}
        <SelectField
          label="Promotion Type"
          value={newPromotion.promotion_type || ""}
          // Changing type keeps what was entered; fields the type does not use are hidden, not wiped.
          onChange={(value) => setNewPromotion({ ...newPromotion, promotion_type: value })}
          options={promotionTypes}
          placeholder="Select promotion type"
          required
        />

        {/* Step 3: Products */}
        <ProductMultiSelect
          label="Products"
          selectedIds={newPromotion.product_ids || []}
          selectedLabels={newPromotion.products}
          onChange={(productIds, products) => setNewPromotion({ ...newPromotion, product_ids: productIds, products })}
          onProductPicked={remember_product}
          required={!layout.products_optional}
          allowAllProducts={layout.products_optional}
        />

        {/* Step 4: Customer and Retailer */}
        <div class="grid grid-cols-2 gap-3">
          <PromotionAccountFields
            customer={newPromotion.customer || ""}
            retailer={newPromotion.retailer || ""}
            onCustomerChange={(value) => setNewPromotion({ ...newPromotion, customer: value })}
            onRetailerChange={(value) => setNewPromotion({ ...newPromotion, retailer: value })}
            calendars={[calendar]}
            showHelp={false}
          />
        </div>
        <p class="text-xs text-gray-500 mt-1">
          Customer is the distributor buying from the brand. Retailer is where consumers buy the product. 
          Use "Direct" for customer if same as retailer.
        </p>

        {/* Step 5: Discount/Pricing Fields (only show after promotion type is selected) */}
        {newPromotion.promotion_type && (
          <DiscountSection
            promotion={newPromotion}
            onChange={setNewPromotion}
            layout={layout}
            products={products}
          />
        )}

        {/* Step 5b: Planned Amount */}
        {newPromotion.promotion_type && (
          <PromotionFormField
            label="Planned Amount"
            value={newPromotion.planned_amount}
            onChange={(value) => setNewPromotion({ ...newPromotion, planned_amount: value as number | undefined })}
            type="number"
            step="0.01"
            prefix="$"
            placeholder="Total expected spend for this promotion"
            help={planned_amount_help(layout)}
          />
        )}
        {newPromotion.promotion_type && <PromotionLastRunNotice promotion={newPromotion} />}

        {/* Step 5c: Volume forecast */}
        {newPromotion.promotion_type && layout.show_volume && (
          <div class="grid grid-cols-2 gap-3">
            <VolumeFields promotion={newPromotion} onChange={setNewPromotion} products={products} />
          </div>
        )}

        {/* Step 5d: Lift and participation assumptions */}
        {newPromotion.promotion_type && layout.show_volume && (
          <PlanningAssumptionsSection promotion={newPromotion} onChange={setNewPromotion} products={products} />
        )}

        {/* Step 6: Basic Details */}
        {newPromotion.promotion_type && (
          <TimingSection
            promotion={newPromotion}
            onChange={setNewPromotion}
            layout={layout}
          />
        )}

        {/* Step 7: More Details (collapsed by default) */}
        {newPromotion.promotion_type && (
          <MoreDetailsSection
            promotion={newPromotion}
            onChange={setNewPromotion}
            showMoreDetails={showMoreDetails}
            onToggleMoreDetails={setShowMoreDetails}
          />
        )}

        <div class="flex gap-2">
          <Button onClick={handleSave} disabled={isSaving} class="flex-1">
            {isSaving ? "Adding..." : "Add Promotion"}
          </Button>
          <Button onClick={onCancel} variant="outline" disabled={isSaving}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  )
}

// Helper components for form sections

interface SectionProps {
  promotion: Promotion
  onChange: (promotion: Promotion) => void
}

/**
 * Read-only per-unit rates aggregated from a promotion's imported line items.
 * Renders nothing when the lines carry no rates. `heading` adds its own section
 * heading for contexts where the editable Discount Details section is absent.
 */
export function LineRatesPanel({ lines, heading }: { lines?: PromotionLine[]; heading?: string }) {
  const rate_chips = summarize_line_rates(lines ?? [])
  const charge_pct = summarize_charge_pct(lines ?? [])
  if (rate_chips.length === 0 && !charge_pct) return null
  return (
    <div class="space-y-2">
      {heading && <h6 class="text-sm font-medium text-gray-700 border-t pt-3">{heading}</h6>}
      <div class="rounded-md border border-gray-200 bg-gray-50 p-2.5 space-y-1.5">
        <p class="text-xs text-gray-500">
          Per-unit rates from this promotion's imported line items (see the Lines tab for the per-product breakdown):
        </p>
        <div class="flex flex-wrap gap-1.5">
          {rate_chips.map(chip => (
            <span key={chip.kind} class="inline-flex items-center rounded-full bg-fuchsia-50 border border-fuchsia-200 px-2 py-0.5 text-xs font-medium text-fuchsia-800">
              {format_rate_chip(chip)}
            </span>
          ))}
          {charge_pct && (
            <span class="inline-flex items-center rounded-full bg-blue-50 border border-blue-200 px-2 py-0.5 text-xs font-medium text-blue-800">
              {format_charge_pct(charge_pct)}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

/** The intro line under the deal-terms heading, by what the type asks for. */
function deal_terms_intro(layout: PromotionTypeLayout): string {
  if (layout.deal_fields === "multibuy") return 'The offer shoppers see, e.g. 2 for $6, and the regular price it is taken off.'
  if (layout.deal_fields === "bogo") return "What the shopper buys and gets free, and what each free unit costs you."
  if (layout.discount_optional) {
    return "Optional for this type — if the deal is a flat amount, put it in Planned Amount. If there is also a price reduction, enter it per unit here."
  }
  return "The deal terms per unit — what the price reduction is, not your total budget (that's Planned Amount)."
}

/** PURE: the Regular Price help, naming where a suggested price came from. */
export function regular_price_help(source: string | null, layout: PromotionTypeLayout): string {
  const what =
    layout.deal_fields === "multibuy"
      ? "the discount per unit is worked out from the deal price against it"
      : layout.deal_fields === "bogo"
        ? "it is the cost of a free unit unless you enter one below"
        : "a discount % is turned into dollars against it"
  const from = source ? ` Suggested from ${source}; change it if the shelf price differs.` : ""
  return `Regular shelf price for one unit before the deal — ${what}.${from}`
}

/** Today as the ISO date the price lookups compare effective dates against. */
function today_iso(): string {
  return new Date().toISOString().slice(0, 10)
}

/**
 * PURE: the regular price to suggest for the promotion as a whole — the one price every picked
 * product agrees on. Products at different prices get their own in the per-product rows instead.
 */
export function suggested_header_price(
  product_ids: string[] | undefined,
  products: ProductDetails,
  customer: string | undefined,
  retailer: string | undefined,
  today: string,
): { amount: number; source: string } | null {
  const suggestions = (product_ids ?? []).map((id) => products[id]).filter(Boolean).map((product) => suggested_regular_price(product, customer, retailer, today))
  if (suggestions.length === 0 || suggestions.some((suggestion) => suggestion == null)) return null
  const amounts = new Set(suggestions.map((suggestion) => suggestion!.amount))
  return amounts.size === 1 ? suggestions[0] : null
}

export function DiscountSection({
  promotion,
  onChange,
  layout,
  lines,
  products = {},
}: SectionProps & { layout: PromotionTypeLayout; lines?: PromotionLine[]; products?: ProductDetails }) {
  // A type with no deal terms (fees, slotting, accruals) gets no heading or helper text over
  // nothing — only the imported per-unit rates, if its lines carry any.
  if (layout.deal_fields === "none") {
    return <LineRatesPanel lines={lines} heading="Discount Details" />
  }
  const discountFields = {
    showAmount: layout.deal_fields === "discount",
    showPercentage: layout.deal_fields === "discount",
    showMultibuy: layout.deal_fields === "multibuy",
    showBogo: layout.deal_fields === "bogo",
  }
  const suggestion = suggested_header_price(promotion.product_ids, products, promotion.customer, promotion.retailer, today_iso())
  // Fill the regular price from the catalog once, when the field is still blank. The rep can
  // always overwrite it; a different product set suggests again only while it is blank.
  useEffect(() => {
    if (promotion.regular_price == null && suggestion) onChange({ ...promotion, regular_price: suggestion.amount })
  }, [suggestion?.amount, promotion.regular_price == null])
  const unit = unit_discount(promotion)
  const on_deal = promoted_price(promotion)
  const heading = discountFields.showMultibuy ? "Multi-Buy Details" : discountFields.showBogo ? "BOGO Details" : "Discount Details"

  return (
    <div class="space-y-3">
      <h6 class="text-sm font-medium text-gray-700 border-t pt-3">{heading}</h6>
      <p class="text-xs text-gray-500">{deal_terms_intro(layout)}</p>

      <LineRatesPanel lines={lines} />

      {discountFields.showMultibuy && (
        <div class="grid grid-cols-2 gap-3">
          <PromotionFormField
            label="Quantity"
            value={promotion.multibuy_quantity}
            onChange={(value) => onChange({ ...promotion, multibuy_quantity: value as number })}
            type="number"
            placeholder="e.g., 2"
            help='Number of units in the multibuy deal, e.g. the "2" in 2-for-$6.'
            required
          />
          <PromotionFormField
            label="Total Price"
            value={promotion.multibuy_price}
            onChange={(value) => onChange({ ...promotion, multibuy_price: value as number })}
            type="number"
            step="0.01"
            prefix="$"
            placeholder="e.g., 6.00"
            help='Shelf price for that quantity, e.g. the "$6" in 2-for-$6.'
            required
          />
        </div>
      )}

      {discountFields.showBogo && (
        <div class="grid grid-cols-3 gap-3">
          <PromotionFormField
            label="Buy"
            value={promotion.bogo_buy_quantity}
            onChange={(value) => onChange({ ...promotion, bogo_buy_quantity: value as number | undefined })}
            type="number"
            placeholder="e.g., 1"
            suffix="units"
            help="How many the shopper buys at full price."
          />
          <PromotionFormField
            label="Get free"
            value={promotion.bogo_get_quantity}
            onChange={(value) => onChange({ ...promotion, bogo_get_quantity: value as number | undefined })}
            type="number"
            placeholder="e.g., 1"
            suffix="units"
            help="How many come free with them."
          />
          <PromotionFormField
            label="Cost of a free unit"
            value={promotion.bogo_free_unit_cost}
            onChange={(value) => onChange({ ...promotion, bogo_free_unit_cost: value as number | undefined })}
            type="number"
            step="0.01"
            prefix="$"
            placeholder={promotion.regular_price ? promotion.regular_price.toFixed(2) : "0.00"}
            help="What the retailer bills you per free unit. Blank uses the regular price."
          />
        </div>
      )}

      {(discountFields.showAmount || discountFields.showPercentage) && (
        <div class="grid grid-cols-2 gap-3">
          {discountFields.showAmount && (
            <PromotionFormField
              label="Discount per Unit"
              value={promotion.discount_amount}
              onChange={(value) => onChange({ ...promotion, discount_amount: value as number | undefined })}
              type="number"
              step="0.01"
              prefix="$"
              placeholder="0.00"
              help="Dollars off each single unit sold on the deal — per unit, not per case. Enter 1.50 for $1.50 off."
            />
          )}
          {discountFields.showPercentage && (
            <PromotionFormField
              label="Discount Percentage"
              value={promotion.discount_percentage}
              onChange={(value) => onChange({ ...promotion, discount_percentage: value as number | undefined })}
              type="number"
              step="0.1"
              suffix="%"
              placeholder="0.0"
              help={
                promotion.regular_price
                  ? "Percent off the regular price, e.g. 15 for a 15% TPR. With a regular price it is turned into dollars per unit; a $ per unit, if entered, wins."
                  : "Percent off the regular price, e.g. 15 for a 15% TPR. Enter the regular price and it is turned into dollars per unit."
              }
            />
          )}
        </div>
      )}

      <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
        <PromotionFormField
          label="Regular Price"
          value={promotion.regular_price}
          onChange={(value) => onChange({ ...promotion, regular_price: value as number | undefined })}
          type="number"
          step="0.01"
          prefix="$"
          placeholder="per unit"
          help={regular_price_help(suggestion?.source ?? null, layout)}
          id="promotion-regular-price"
        />
        {unit && (
          <div class="self-end rounded-md border border-gray-200 bg-gray-50 p-2.5 text-xs text-gray-700">
            <div>
              Costs you <span class="font-semibold">{format_money(unit.amount)}</span> per unit sold on the deal
              {unit.working ? ` (${unit.working})` : ""}.
            </div>
            {on_deal != null && promotion.regular_price ? (
              <div class="mt-0.5 text-gray-500">
                Shelf price on the deal {format_money(on_deal)}, from {format_money(promotion.regular_price)}.
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  )
}

/** The per-product rows, folded away until a rep needs them so a one-product deal stays simple. */
function PerProductSection({
  promotion,
  onChange,
  products,
  open,
  onToggle,
}: SectionProps & { products: ProductDetails; open: boolean; onToggle: (open: boolean) => void }) {
  const count = Math.max(promotion.product_ids?.length ?? 0, promotion.products.length)
  return (
    <div class="space-y-2">
      <button type="button" onClick={() => onToggle(!open)} class="text-sm font-medium text-blue-600 hover:text-blue-800">
        {open ? "Hide" : "Set"} discount and volume per product ({count})
      </button>
      {open && <PromotionLinesEditor promotion={promotion} onChange={onChange} products={products} />}
    </div>
  )
}

/**
 * Planned volume in units and cases, each following the other through the picked products' case
 * pack when they all share one (Bakhrom: "Units and cases aren't linked. I entered 10,000 units
 * and 500 cases (20 per case) with no check or auto-conversion").
 */
export function VolumeFields({ promotion, onChange, products = {} }: SectionProps & { products?: ProductDetails }) {
  const picked = (promotion.product_ids ?? []).map((id) => products[id]).filter(Boolean)
  const pack = shared_units_per_case(picked) ?? undefined
  const packs_differ = picked.length > 1 && pack == null && picked.some((product) => product.units_per_case)
  const disagree = volumes_disagree(promotion.planned_volume_units, promotion.planned_volume_cases, pack)
  const totals = promotion.lines && promotion.lines.length > 0 ? line_totals(promotion.lines, promotion) : null
  const from_lines = totals != null && (totals.units > 0 || totals.cases > 0)

  function set_units(units: number | undefined) {
    onChange({ ...promotion, planned_volume_units: units, planned_volume_cases: cases_from_units(units, pack) ?? promotion.planned_volume_cases })
  }
  function set_cases(cases: number | undefined) {
    onChange({ ...promotion, planned_volume_cases: cases, planned_volume_units: units_from_cases(cases, pack) ?? promotion.planned_volume_units })
  }

  return (
    <>
      <PromotionFormField
        label="Planned Volume (units)"
        value={promotion.planned_volume_units}
        onChange={(value) => set_units(value as number | undefined)}
        type="number"
        step="1"
        placeholder="e.g., 60,000"
        help={
          from_lines
            ? `Consumer units this event is expected to move, including the lift. The per-product rows add up to ${Math.round(totals!.units).toLocaleString("en-US")}.`
            : "Consumer units this event is expected to move, including the lift. The estimate is paid on these."
        }
        id="promotion-volume-units"
      />
      <PromotionFormField
        label="Planned Volume (cases)"
        value={promotion.planned_volume_cases}
        onChange={(value) => set_cases(value as number | undefined)}
        type="number"
        step="1"
        placeholder="e.g., 5,000"
        help={
          pack
            ? `Same volume in cases at ${pack} per case — filled in from the units, or type cases and the units follow.`
            : packs_differ
              ? "The picked products have different case packs, so cases are not converted here — set volume per product below."
              : "Same volume in cases, if that is how you plan. Pick catalog products with a case pack and the two convert."
        }
        error={disagree}
      />
      {disagree && pack && (
        <p class="col-span-full -mt-1 text-xs text-red-700">
          Units and cases disagree at {pack} per case: {Math.round(promotion.planned_volume_units ?? 0).toLocaleString("en-US")} units is{" "}
          {cases_from_units(promotion.planned_volume_units, pack)?.toLocaleString("en-US")} cases. The estimate uses the units.
        </p>
      )}
    </>
  )
}

/**
 * What the deal does to margin on one unit, from the regular price, a cost per unit (the catalog's
 * when it has one, else typed here) and the deal's cost per unit. Shown with the estimate; the
 * cost is not saved on the promotion.
 */
export function MarginImpactPanel({ promotion, products = {}, unit_cost, onUnitCostChange }: { promotion: Promotion; products?: ProductDetails; unit_cost: number | undefined; onUnitCostChange: (cost: number | undefined) => void }) {
  const unit = unit_discount(promotion)
  const catalog_costs = (promotion.product_ids ?? []).map((id) => products[id]).filter(Boolean).map(suggested_unit_cost).filter((cost): cost is number => cost != null)
  const catalog_cost = catalog_costs.length > 0 && new Set(catalog_costs).size === 1 ? catalog_costs[0] : undefined
  const effective_cost = unit_cost ?? catalog_cost
  const impact = margin_impact(promotion.regular_price, effective_cost, unit?.amount)
  if (!promotion.regular_price || !unit) return null
  return (
    <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
      <PromotionFormField
        label="Your cost per unit"
        value={unit_cost ?? catalog_cost}
        onChange={(value) => onUnitCostChange(value as number | undefined)}
        type="number"
        step="0.01"
        prefix="$"
        placeholder="0.00"
        help={catalog_cost != null ? "From the catalog's cost of goods; change it to see a different margin. Not saved on the promotion." : "Cost of goods for one unit, to see what the deal does to margin. Not saved on the promotion."}
      />
      {impact ? (
        <div class={`self-end rounded-md border p-2.5 text-xs ${impact.promoted_margin < 0 ? "border-red-200 bg-red-50 text-red-800" : "border-gray-200 bg-gray-50 text-gray-700"}`}>
          <div>
            Margin {format_money(impact.regular_margin)} ({format_pct(impact.regular_margin_pct)}) → <span class="font-semibold">{format_money(impact.promoted_margin)} ({format_pct(impact.promoted_margin_pct)})</span> on the deal.
          </div>
          <div class="mt-0.5 text-gray-500">{format_money(impact.per_unit)} of margin given up per unit sold.</div>
        </div>
      ) : (
        <p class="self-end text-xs text-gray-500">Enter your cost per unit to see the margin before and after the deal.</p>
      )}
    </div>
  )
}

/**
 * Plan-time lift and participation assumptions. These are what a planner knows before a retailer
 * has any history: how much extra volume the deal should drive, and — for chains whose stores opt
 * in individually — what share of units the rate will actually be paid on. When a per-unit rate and
 * a unit volume are present the section shows the resulting trade estimate and offers it as the
 * planned amount. `showVolume` renders the planned-volume inputs inside the section for forms that
 * do not already carry them (the create wizard).
 */
export function PlanningAssumptionsSection({
  promotion,
  onChange,
  showVolume,
  products = {},
  unit_cost,
  onUnitCostChange,
}: SectionProps & { showVolume?: boolean; products?: ProductDetails; unit_cost?: number; onUnitCostChange?: (cost: number | undefined) => void }) {
  // When the per-product rows carry a spend, they are the estimate; the header's rate × volume
  // is for a promotion planned at the top level.
  const totals = promotion.lines && promotion.lines.length > 0 ? line_totals(promotion.lines, promotion) : null
  const estimate = totals && totals.spend > 0 ? totals.spend : expected_trade_spend(promotion)
  const estimate_cents = estimate == null ? null : Math.round(estimate * 100)
  const planned_cents = promotion.planned_amount == null ? null : Math.round(promotion.planned_amount * 100)
  const estimate_differs = estimate_cents != null && estimate_cents !== planned_cents
  const trade_note = expected_trade_note(promotion)
  const baseline = split_baseline_and_lift(promotion.planned_volume_units, promotion.expected_lift_pct)
  const [local_cost, set_local_cost] = useState<number | undefined>(undefined)
  const cost = onUnitCostChange ? unit_cost : local_cost
  const set_cost = onUnitCostChange ?? set_local_cost
  return (
    <div class="space-y-3">
      <h6 class="text-sm font-medium text-gray-700 border-t pt-3">Volume, Lift &amp; Participation</h6>
      <p class="text-xs text-gray-500">
        Starting assumptions for a retailer with little or no history. Once the promotion has run, the Insights tab
        shows measured lift next to what you expected here.
      </p>

      {showVolume && (
        <div class="grid grid-cols-2 gap-3">
          <VolumeFields promotion={promotion} onChange={onChange} products={products} />
        </div>
      )}

      <div class="grid grid-cols-2 gap-3">
        <PromotionFormField
          label="Expected Lift (%)"
          value={promotion.expected_lift_pct}
          onChange={(value) => onChange({ ...promotion, expected_lift_pct: value as number | undefined })}
          type="number"
          step="1"
          placeholder="e.g., 20"
          help={
            baseline
              ? `Extra volume you expect versus a normal week. Planned Volume already includes it: of ${Math.round(promotion.planned_volume_units!).toLocaleString("en-US")} units, about ${baseline.baseline.toLocaleString("en-US")} is baseline and ${baseline.lift.toLocaleString("en-US")} the lift. Compared with the lift measured after the promo runs.`
              : "Extra volume you expect versus a normal week — 20 means +20%. Enter Planned Volume with the lift already included; this is saved to compare with the lift measured after the promo runs."
          }
        />
        <PromotionFormField
          label="Store Participation (%)"
          value={promotion.participation_pct}
          onChange={(value) => onChange({ ...promotion, participation_pct: value as number | undefined })}
          type="number"
          step="1"
          placeholder="100"
          help="Share of stores you expect to run the deal; the estimate is paid on that share of the units. Leave blank when every store runs it (counts as 100%). If you expect 30–40% of an opt-in chain's stores, enter 35."
        />
      </div>

      {trade_note && <p class="text-xs text-gray-600">{trade_note}</p>}

      {estimate != null && (
        <div class="flex flex-wrap items-center justify-between gap-2 rounded-md border border-blue-200 bg-blue-50 p-2.5 text-xs text-blue-800">
          <span>
            Expected trade:{" "}
            {totals && totals.spend > 0 ? `the per-product rows add up to` : format_trade_spend_formula(promotion)} ≈{" "}
            <span class="font-semibold">{exactCurrencyFormatter(estimate)}</span>
          </span>
          {estimate_differs && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onChange({ ...promotion, planned_amount: estimate })}
            >
              Use as planned amount
            </Button>
          )}
        </div>
      )}

      <MarginImpactPanel promotion={promotion} products={products} unit_cost={cost} onUnitCostChange={set_cost} />
    </div>
  )
}

function TimingSection({ promotion, onChange, layout }: SectionProps & { layout: PromotionTypeLayout }) {
  return (
    <div class="space-y-3">
      <h6 class="text-sm font-medium text-gray-700 border-t pt-3">Timing</h6>
      <PromotionFormField
        label="Description"
        value={promotion.description}
        onChange={(value) => onChange({ ...promotion, description: value as string })}
        placeholder="Optional description"
      />

      <div class="grid grid-cols-2 gap-3">
        <PromotionFormField
          label="Start Date"
          value={promotion.start_date}
          onChange={(value) => onChange({ ...promotion, start_date: value as string })}
          type="date"
          help={start_date_help(layout)}
        />
        <PromotionFormField
          label="End Date"
          value={promotion.end_date}
          onChange={(value) => onChange({ ...promotion, end_date: value as string })}
          type="date"
        />
      </div>
    </div>
  )
}

function MoreDetailsSection({ 
  promotion, 
  onChange, 
  showMoreDetails, 
  onToggleMoreDetails 
}: SectionProps & { 
  showMoreDetails: boolean
  onToggleMoreDetails: (show: boolean) => void 
}) {
  return (
    <div class="space-y-3">
      <button
        type="button"
        onClick={() => onToggleMoreDetails(!showMoreDetails)}
        class="text-sm text-blue-600 hover:text-blue-800 font-medium"
      >
        {showMoreDetails ? "Hide" : "Add more details"} (regions, terms, restrictions)
      </button>

      {showMoreDetails && (
        <div class="space-y-3 border-t pt-3">
          <ArrayField
            label="Regions"
            value={promotion.regions || []}
            onChange={(value) => onChange({ ...promotion, regions: value })}
          />

          <ArrayField
            label="Terms"
            value={promotion.terms || []}
            onChange={(value) => onChange({ ...promotion, terms: value })}
            multiline={true}
          />

          <ArrayField
            label="Restrictions"
            value={promotion.restrictions || []}
            onChange={(value) => onChange({ ...promotion, restrictions: value })}
          />
        </div>
      )}
    </div>
  )
}
